import { useState, useCallback, useEffect, useRef } from 'react';
import { taskApi } from '../../../../tasks/api/taskApi';
import type { Task } from '../../../../tasks/types/task.types';
import type { DependencyGraph } from './useGanttDependencies';

export interface DragState {
  taskId: string;
  originalLeft: number;
  currentLeft: number;
  originalStartDate: Date;
  originalDueDate: Date;
  newStartDate: Date;
  newDueDate: Date;
  daysDelta: number;
  barWidth: number;
  affectedTaskIds: string[];
  blockedByMandated: boolean;
  blockedTaskTitle?: string;
}

export interface PendingChange {
  taskId: string;
  title: string;
  oldStartDate: string;
  oldDueDate: string;
  newStartDate: string;
  newDueDate: string;
}

export type DragSaveResult = 'idle' | 'success' | 'mandated_conflict' | 'cascade_violation' | 'error';

export interface DragSaveErrorDetail {
  error?: string;
  message: string;
  task_id?: string;
  task_title?: string;
  blocking_task_id?: string;
  blocking_task_title?: string;
  blocking_task_due_date?: string;
}

export interface ConfirmDragResult {
  result: DragSaveResult;
  updated?: number;
  tasks?: Task[];
  errorDetail?: DragSaveErrorDetail;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function isCourtMandated(task: Task): boolean {
  return !!(task.court_metadata?.is_court_mandated || task.source === 'court_rule');
}

function normalizeApiErrorDetail(error: unknown): DragSaveErrorDetail | null {
  const err = error as any;
  const payload = err?.details || err?.response?.data;
  if (payload && typeof payload === 'object') {
    return {
      error: payload.error,
      message: payload.message || payload.error || 'Request failed.',
      task_id: payload.task_id,
      task_title: payload.task_title,
      blocking_task_id: payload.blocking_task_id,
      blocking_task_title: payload.blocking_task_title,
      blocking_task_due_date: payload.blocking_task_due_date,
    };
  }
  if (typeof err?.message === 'string' && err.message.trim()) {
    return { message: err.message };
  }
  return null;
}

/**
 * BFS through downstream dependency graph to find all tasks that would be
 * affected by moving a task forward by `daysDelta` days.
 * Returns the list of affected tasks and whether the move is blocked by a mandated deadline.
 */
function calculateCascade(
  draggedTaskId: string,
  daysDelta: number,
  graph: DependencyGraph,
  allTasks: Task[],
): { affected: PendingChange[]; blocked: boolean; blockedTaskTitle?: string } {
  // Moving backward: only the dragged task moves — safe, no cascade needed
  if (daysDelta <= 0) {
    return { affected: [], blocked: false };
  }

  const affected: PendingChange[] = [];
  const visited = new Set<string>();
  const queue: string[] = [draggedTaskId];
  const taskMap = new Map(allTasks.map(t => [t.id, t]));

  while (queue.length > 0) {
    const currentId = queue.shift()!;
    const downstreamIds = graph.downstream.get(currentId) || [];

    for (const depId of downstreamIds) {
      if (visited.has(depId)) continue;
      visited.add(depId);

      const depTask = taskMap.get(depId);
      if (!depTask) continue;

      // Court-mandated tasks are immovable walls
      if (isCourtMandated(depTask)) {
        // Check if the cascade would push past this mandated deadline
        if (depTask.due_date) {
          const depDueDate = new Date(depTask.due_date);
          // Find the task directly upstream of this mandated task and check if its new date exceeds the mandated deadline
          const upstreamTask = taskMap.get(currentId);
          if (upstreamTask?.due_date) {
            const upstreamNewDue = addDays(
              new Date(upstreamTask.due_date),
              currentId === draggedTaskId ? daysDelta : daysDelta // all affected shift by same delta
            );
            if (upstreamNewDue > depDueDate) {
              return { affected, blocked: true, blockedTaskTitle: depTask.title };
            }
          }
        }
        // Mandated task does not move, cascade stops here for this branch
        continue;
      }

      // Non-mandated: shift by same N days
      const depStartDate = depTask.start_date
        ? new Date(depTask.start_date)
        : depTask.created_at
          ? new Date(depTask.created_at)
          : new Date();
      const depDueDate = depTask.due_date
        ? new Date(depTask.due_date)
        : addDays(depStartDate, 7);

      affected.push({
        taskId: depId,
        title: depTask.title,
        oldStartDate: depTask.start_date || depStartDate.toISOString(),
        oldDueDate: depTask.due_date || depDueDate.toISOString(),
        newStartDate: addDays(depStartDate, daysDelta).toISOString(),
        newDueDate: addDays(depDueDate, daysDelta).toISOString(),
      });

      // Continue cascade through this task's dependents
      queue.push(depId);
    }
  }

  return { affected, blocked: false };
}

interface UseGanttDragParams {
  matterId: string;
  tasks: Task[];
  dependencyGraph: DependencyGraph;
  dayWidth: number;
  startDate: Date;
  scrollRef: React.RefObject<HTMLDivElement | null>;
  labelColWidth: number;
  getTaskBar: (task: Task) => { left: number; width: number };
}

export function useGanttDrag({
  matterId,
  tasks,
  dependencyGraph,
  dayWidth,
  startDate,
  scrollRef,
  labelColWidth,
  getTaskBar,
}: UseGanttDragParams) {
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<PendingChange[]>([]);
  const [draggedTaskChange, setDraggedTaskChange] = useState<PendingChange | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<DragSaveResult>('idle');
  const [errorDetail, setErrorDetail] = useState<DragSaveErrorDetail | null>(null);

  // Refs for mouse tracking (avoid stale closures)
  const dragStartXRef = useRef(0);
  const dragStateRef = useRef<DragState | null>(null);
  const isDraggingRef = useRef(false);
  const dragThresholdMetRef = useRef(false);

  const taskMap = useRef(new Map<string, Task>());
  useEffect(() => {
    taskMap.current = new Map(tasks.map(t => [t.id, t]));
  }, [tasks]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDraggingRef.current || !dragStateRef.current) return;

    const deltaX = e.clientX - dragStartXRef.current;

    // Require at least 3px of movement before starting visual drag
    if (!dragThresholdMetRef.current) {
      if (Math.abs(deltaX) < 3) return;
      dragThresholdMetRef.current = true;
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'grabbing';
    }

    const daysDelta = Math.round(deltaX / dayWidth);
    const snappedLeft = dragStateRef.current.originalLeft + daysDelta * dayWidth;
    const newStartDate = addDays(dragStateRef.current.originalStartDate, daysDelta);
    const newDueDate = addDays(dragStateRef.current.originalDueDate, daysDelta);

    // Calculate cascade
    const cascade = calculateCascade(
      dragStateRef.current.taskId,
      daysDelta,
      dependencyGraph,
      tasks,
    );

    const updatedState: DragState = {
      ...dragStateRef.current,
      currentLeft: snappedLeft,
      newStartDate,
      newDueDate,
      daysDelta,
      affectedTaskIds: cascade.affected.map(a => a.taskId),
      blockedByMandated: cascade.blocked,
      blockedTaskTitle: cascade.blockedTaskTitle,
    };

    dragStateRef.current = updatedState;
    setDragState(updatedState);

    // Auto-scroll when near edges
    const scrollContainer = scrollRef.current;
    if (scrollContainer) {
      const rect = scrollContainer.getBoundingClientRect();
      const edgeThreshold = 40;
      if (e.clientX > rect.right - edgeThreshold) {
        scrollContainer.scrollLeft += 8;
      } else if (e.clientX < rect.left + labelColWidth + edgeThreshold) {
        scrollContainer.scrollLeft -= 8;
      }
    }
  }, [dayWidth, dependencyGraph, tasks, scrollRef, labelColWidth]);

  const handleMouseUp = useCallback(() => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';

    const state = dragStateRef.current;
    isDraggingRef.current = false;

    if (!state || !dragThresholdMetRef.current || state.daysDelta === 0) {
      // No meaningful drag — clean up
      setDragState(null);
      dragStateRef.current = null;
      return;
    }

    if (state.blockedByMandated) {
      // Move blocked by mandated deadline — cancel
      setDragState(null);
      dragStateRef.current = null;
      return;
    }

    // Build pending changes
    const task = taskMap.current.get(state.taskId);
    if (!task) {
      setDragState(null);
      dragStateRef.current = null;
      return;
    }

    const mainChange: PendingChange = {
      taskId: state.taskId,
      title: task.title,
      oldStartDate: task.start_date || '',
      oldDueDate: task.due_date || '',
      newStartDate: state.newStartDate.toISOString(),
      newDueDate: state.newDueDate.toISOString(),
    };

    const cascade = calculateCascade(state.taskId, state.daysDelta, dependencyGraph, tasks);

    setDraggedTaskChange(mainChange);
    setPendingChanges(cascade.affected);
    setShowConfirmDialog(true);
  }, [handleMouseMove, dependencyGraph, tasks]);

  const handleMouseDown = useCallback((taskId: string, e: React.MouseEvent) => {
    const task = taskMap.current.get(taskId);
    if (!task) return;

    // Court-mandated tasks cannot be dragged
    if (isCourtMandated(task)) return;

    e.preventDefault();
    e.stopPropagation();

    const bar = getTaskBar(task);
    const taskStart = task.start_date ? new Date(task.start_date) : task.created_at ? new Date(task.created_at) : new Date();
    const taskEnd = task.due_date ? new Date(task.due_date) : addDays(taskStart, 7);

    const initialState: DragState = {
      taskId,
      originalLeft: bar.left,
      currentLeft: bar.left,
      originalStartDate: taskStart,
      originalDueDate: taskEnd,
      newStartDate: taskStart,
      newDueDate: taskEnd,
      daysDelta: 0,
      barWidth: bar.width,
      affectedTaskIds: [],
      blockedByMandated: false,
    };

    dragStateRef.current = initialState;
    dragStartXRef.current = e.clientX;
    isDraggingRef.current = true;
    dragThresholdMetRef.current = false;

    setDragState(initialState);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [getTaskBar, handleMouseMove, handleMouseUp]);

  const confirmDrag = useCallback(async (): Promise<ConfirmDragResult> => {
    if (!matterId || !draggedTaskChange) {
      const detail = { message: 'No pending drag changes to save.' };
      setSaveResult('error');
      setErrorDetail(detail);
      return { result: 'error', errorDetail: detail };
    }

    const allChanges = [draggedTaskChange, ...pendingChanges];
    const changes = allChanges.map(change => ({
      task_id: change.taskId,
      new_start_date: new Date(change.newStartDate).toISOString(),
      new_due_date: new Date(change.newDueDate).toISOString(),
    }));

    try {
      setSaving(true);
      setSaveResult('idle');
      setErrorDetail(null);

      const response = await taskApi.bulkReschedule(matterId, changes) as any;
      const updatedTasks: Task[] = response.tasks || response.data?.tasks || [];
      const updatedCount: number = response.updated ?? response.data?.updated ?? updatedTasks.length;

      setShowConfirmDialog(false);
      setSaveResult('success');
      return { result: 'success', updated: updatedCount, tasks: updatedTasks };
    } catch (error) {
      const status = (error as any)?.status ?? (error as any)?.statusCode;
      const detail = normalizeApiErrorDetail(error);

      if (status === 409 && detail?.error === 'MANDATED_TASK_CONFLICT') {
        setSaveResult('mandated_conflict');
        setErrorDetail(detail);
        return { result: 'mandated_conflict', errorDetail: detail };
      }

      if (status === 409 && detail?.error === 'CASCADE_WOULD_VIOLATE_DEADLINE') {
        setSaveResult('cascade_violation');
        setErrorDetail(detail);
        return { result: 'cascade_violation', errorDetail: detail };
      }

      const fallbackDetail = detail || { message: 'Failed to reschedule tasks.' };
      setSaveResult('error');
      setErrorDetail(fallbackDetail);
      return { result: 'error', errorDetail: fallbackDetail };
    } finally {
      setSaving(false);
    }
  }, [matterId, draggedTaskChange, pendingChanges]);

  const rejectDrag = useCallback(() => {
    setDragState(null);
    dragStateRef.current = null;
    setShowConfirmDialog(false);
    setPendingChanges([]);
    setDraggedTaskChange(null);
    setSaving(false);
    setSaveResult('idle');
    setErrorDetail(null);
  }, []);

  const clearDragState = useCallback(() => {
    setDragState(null);
    dragStateRef.current = null;
    setPendingChanges([]);
    setDraggedTaskChange(null);
    setSaveResult('idle');
    setErrorDetail(null);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [handleMouseMove, handleMouseUp]);

  return {
    dragState,
    handleMouseDown,
    showConfirmDialog,
    pendingChanges,
    draggedTaskChange,
    saving,
    saveResult,
    errorDetail,
    confirmDrag,
    rejectDrag,
    clearDragState,
    isCourtMandated,
  };
}
